import { PageTemplate } from '@/shared/ui/templates';
import styles from './index.module.sass';
import { SelectBuyer } from '../../components/select-buyer';
import { ProfitBadge } from '../../components/profit-badge';
import { CalculatorTable } from '../../components/calculator-table';
import { CalculatorCard } from '../../components/calculator-card';
import { SelectCalculatorFields } from '../../components/select-calculator-fields';
import { useEffect, useState } from 'react';
import {
  $rustmixBuyerPrices,
  $rustmixBuyers,
  $selectedRustmixBuyer,
  fetchRustmixBuyerPricesFx,
  fetchRustmixBuyersFx,
  setSelectedRustmixBuyer,
} from '../../store/calculator.store';
import { useStore } from 'effector-react';
import { ICalculatorBag, IRustmixBuyer } from '../../ts/rustmix-buyer.types';
import {
  handleTotalData,
  loadBagsDataToLocalStorage,
  loadFieldsFromLocalstorage,
} from '../../helpers';
import {
  useResData,
  useTotalData,
  useBag,
  useFields,
  useTargetMoisture,
} from '../../hooks';
import { toggleField } from '../../helpers';
import { CalculatorSelectSourceModal } from '../../components/calculator-select-source-modal';
import { InputWithSign } from '@/modules/common/components/input-with-sign';
import { Checkbox, Switch, notification } from 'antd';
import { $mixedBags, removeBag } from '@/entities/simple-mix/model';
import { MAX_TARGET_MOISTURE } from '../../constants/calculator.constants';
import {
  $selectedLeftWh,
  $selectedRightWh,
  toggleLeftWh,
  toggleRightWh,
  toggleSelectWarehouse,
} from '@/widgets/storage-viewer/model';
import { ExpertModeControls } from '../../components/calculator-expert';

export const CalculatorPage = () => {
  // LOCAL STATES
  const { fields, setFields } = useFields();
  const { bag: bagA, setBag: setBagA, bagInitialState } = useBag();
  const { bag: bagB, setBag: setBagB } = useBag();
  const { totalData, setTotalData } = useTotalData();
  const { resData, setResData } = useResData();
  const {
    state: targetMoistureData,
    setTargetMoisture,
    setEnableMoistenUnmixed,
    setShrinkageCoeff,
    setBagAWeightGain,
    setBagBWeightGain,
    setBagMixedWeightGain,
    setBagAPriceGain,
    setBagBPriceGain,
    setBagMixedPriceGain,
    setInitialState,
    setBagAMoistureCoeff,
    setBagBMoistureCoeff,
    setBagMixedMoistureCoeff,
    setState,
  } = useTargetMoisture();
  const [selectSourceModalVisible, setSelectSourceModalVisible] = useState(0);
  const [eventTrigger, setEventTrigger] = useState(0);
  const [expertMode, setExpertMode] = useState(true);
  const [selectedBags, setSelectedBags] = useState<string[]>(['', '']);

  // GLOBAL STATES
  const selectedBuyer = useStore($selectedRustmixBuyer);
  const rustmixBuyerPrices = useStore($rustmixBuyerPrices);
  const mixedBags = useStore($mixedBags);
  const selectedLeftWh = useStore($selectedLeftWh);
  const selectedRightWh = useStore($selectedRightWh);

  // LOADING API DATA AND LOCALSTORAGE STATES
  useEffect(() => {
    // fetchRustmixBuyerPricesFx(rustmixBuyers?.data[0]);

    loadBagsDataToLocalStorage({ setBagA, setBagB, setResData });
    loadFieldsFromLocalstorage({
      setFields,
      setTargetMoisture,
      setEnableMoistenUnmixed,
    });
  }, []);

  // SETTING INTO LOCALSTORAGE ACTUAL DATA
  useEffect(() => {
    localStorage.setItem('bagA', JSON.stringify(bagA));
  }, [bagA]);

  useEffect(() => {
    localStorage.setItem('bagB', JSON.stringify(bagB));
  }, [bagB]);

  useEffect(() => {
    localStorage.setItem('calculatorFields', JSON.stringify(fields));
  }, [fields]);

  useEffect(() => {
    localStorage.setItem(
      'targetMoisture',
      JSON.stringify(targetMoistureData.targetMoisture),
    );
  }, [targetMoistureData.targetMoisture]);

  useEffect(() => {
    localStorage.setItem(
      'enableMoistenUnmixed',
      JSON.stringify(targetMoistureData.enableMoistenUnmixed),
    );
  }, [targetMoistureData.enableMoistenUnmixed]);

  // useEffect(() => {
  //   if (selectedBuyer.id !== -1) fetchRustmixBuyerPricesFx(selectedBuyer);
  // }, [selectedBuyer]);

  useEffect(() => {
    handleTotalData({
      expertMode,
      rustmixBuyerPrices,
      bagA,
      bagB,
      setShrinkageCoeff,
      setState,
      setResData,
      resData,
      setTotalData,
      targetMoistureData,
      setBagAWeightGain,
      setBagBWeightGain,
      setBagMixedWeightGain,
      setBagAPriceGain,
      setBagBPriceGain,
      setBagMixedPriceGain,
      setBagAPrice: (val: number) => setTotalData({ ...totalData, bagAPrice: val }),
      setBagBPrice: (val: number) => setTotalData({ ...totalData, bagBPrice: val }),
      setInitialState,
      setBagAMoistureCoeff,
      setBagBMoistureCoeff,
      setBagMixedMoistureCoeff,
    });

    clearSourceBag();
  }, [
    rustmixBuyerPrices,
    eventTrigger,
    targetMoistureData.targetMoisture,
    targetMoistureData.enableMoistenUnmixed,
    targetMoistureData.shrincageCoeff,
    expertMode,
  ]); // bagA, bagB

  // useEffect(() => {
  //   clearSourceBag();
  //   // uses for clearing source bag when user changes pre-setted parameters of selected sleeve
  // }, [bagA, bagB]);

  // OTHER EFFECTS
  useEffect(() => {
    const val = 100 / (100 - targetMoistureData.targetMoisture);
    setShrinkageCoeff(+val.toFixed(3));
  }, [targetMoistureData.targetMoisture]);

  useEffect(() => {
    if (!expertMode) {
      setTargetMoisture(14);
      setEnableMoistenUnmixed(false);
    }
  }, [expertMode]);

  useEffect(() => {
    setSelectedBags(mixedBags as [string, string]);
  }, [mixedBags]);

  // HELPERS
  const clearBag = ({ side, anonimize }: { side: 'A' | 'B'; anonimize: boolean }) => {
    if (side === 'A') {
      !anonimize && setBagA(bagInitialState);
      setSelectedBags(['', selectedBags[1]]);
      toggleLeftWh(null);
      localStorage.removeItem('L_BAG');
      removeBag('left');
      expertMode && setBagA((state) => ({ ...state, humidity: '14' }));
    } else if (side === 'B') {
      !anonimize && setBagB(bagInitialState);
      setSelectedBags([selectedBags[0], '']);
      toggleRightWh(null);
      localStorage.removeItem('R_BAG');
      removeBag('right');
      expertMode && setBagB((state) => ({ ...state, humidity: '14' }));
    }
    setEventTrigger(eventTrigger + 1);
  };

  const clearSourceBag = () => {
    if (selectedBags[0] && selectedLeftWh) {
      const defaultState = localStorage.getItem('bag_a_initial_state');
      defaultState !== JSON.stringify(bagA) && clearBag({ side: 'A', anonimize: true });
    }

    if (selectedBags[1] && selectedRightWh) {
      const defaultState = localStorage.getItem('bag_b_initial_state');
      defaultState !== JSON.stringify(bagB) && clearBag({ side: 'B', anonimize: true });
    }

    handleTotalData({
      expertMode,
      rustmixBuyerPrices,
      bagA,
      bagB,
      setShrinkageCoeff,
      setState,
      setResData,
      resData,
      setTotalData,
      targetMoistureData,
      setBagAWeightGain,
      setBagBWeightGain,
      setBagMixedWeightGain,
      setBagAPriceGain,
      setBagBPriceGain,
      setBagMixedPriceGain,
      setBagAPrice: (val: number) => setTotalData({ ...totalData, bagAPrice: val }),
      setBagBPrice: (val: number) => setTotalData({ ...totalData, bagBPrice: val }),
      setInitialState,
      setBagAMoistureCoeff,
      setBagBMoistureCoeff,
      setBagMixedMoistureCoeff,
    });
  };

  const profit: number = isNaN(Number(totalData.resProfit))
    ? Math.floor(+totalData.resPrice - (+totalData.bagAPrice + +totalData.bagBPrice))
    : Math.floor(Number(totalData.resProfit));

  return (
    <PageTemplate header="миксование пшеницы">
      <div className={styles.root}>
        <div className={styles.head}>
          <div className={styles.selectBar}>
            <SelectBuyer
              onClick={(buyer: IRustmixBuyer) => {
                setSelectedRustmixBuyer(buyer);
              }}
            />
            <SelectCalculatorFields
              options={fields}
              toggleField={(val: string) => toggleField({ setFields, value: val })}
            />
            <div className={styles.expertMode}>
              <span>
                Продвинутый режим <br />
                (усушка/увлажнение)
              </span>
              <Switch
                checked={expertMode}
                onChange={() => {
                  setExpertMode(!expertMode);
                  setInitialState();
                  setEventTrigger(eventTrigger + 1);
                }}
              />
            </div>

            <div
              className={styles.expertControls}
              style={{
                transition: '.15s',
                opacity: expertMode ? '1' : '0',
                visibility: expertMode ? 'visible' : 'hidden',
              }}
            >
              <ExpertModeControls
                targetMoistureData={targetMoistureData}
                setTargetMoisture={setTargetMoisture}
                setEnableMoistenUnmixed={setEnableMoistenUnmixed}
              />
            </div>
          </div>

          {profit !== 0 && !!totalData.bagAPrice && !!totalData.bagBPrice && (
            <ProfitBadge profit={profit} />
          )}
        </div>
        <div className={styles.content}>
          <CalculatorTable fields={fields} totalData={totalData} />
        </div>
        <div className={styles.bottom}>
          <CalculatorCard
            id={1}
            selectedBagId={selectedBags[0]}
            expertMode={expertMode}
            setValue={(val: any) => setBagA(val)}
            data={bagA}
            color="blue"
            title="компонент 1"
            sum={totalData.bagAPrice || 'цена отсутствует'}
            pricePerTon={+totalData?.bagAPricePerTon}
            renewTotalData={() => setEventTrigger(eventTrigger + 1)}
            selectSourceFx={() => setSelectSourceModalVisible(1)}
            targetMoistureData={targetMoistureData.bagA}
            clearBag={() => clearBag({ side: 'A', anonimize: false })}
            totalData={totalData as any}
            className={styles.compOne}
          />
          <CalculatorCard
            expertMode={expertMode}
            color="green"
            title="результат миксования"
            sum={totalData.resPrice || 'цена отсутствует'}
            profitSum={profit}
            variant="profit"
            pricePerTon={+totalData?.resPricePerTon}
            data={resData as ICalculatorBag}
            targetMoistureData={targetMoistureData.bagMixed}
            totalData={totalData as any}
            className={styles.compResult}
          />
          <CalculatorCard
            id={2}
            selectedBagId={selectedBags[1]}
            expertMode={expertMode}
            setValue={setBagB}
            data={bagB}
            color="red"
            title="компонент 2"
            pricePerTon={+totalData?.bagBPricePerTon}
            sum={totalData?.bagBPrice || 'цена отсутствует'}
            renewTotalData={() => setEventTrigger(eventTrigger + 1)}
            selectSourceFx={() => setSelectSourceModalVisible(2)}
            targetMoistureData={targetMoistureData.bagB}
            clearBag={() => clearBag({ side: 'B', anonimize: false })}
            totalData={totalData as any}
            className={styles.compTwo}
          />
        </div>
      </div>
      <CalculatorSelectSourceModal
        visible={selectSourceModalVisible}
        setVisible={setSelectSourceModalVisible}
        componentNumber={selectSourceModalVisible as 1 | 2}
        setBagA={setBagA}
        setBagB={setBagB}
        recalculateFoo={() => setEventTrigger(eventTrigger + 1)}
        selectedBags={selectedBags as [string, string]}
        setSelectedBags={setSelectedBags}
      />
    </PageTemplate>
  );
};
